import React, { useState, useEffect } from "react";
import { Modal, Input, Switch, Form } from "antd";
import type { Job } from "../../../../core/data/redux/recruitment/types";

interface CloseJobModalProps {
  open: boolean;
  job: Job | null;
  loading: boolean;
  onCancel: () => void;
  onCloseJob: (jobId: number, reason: string, closeOnExternal: boolean) => void;
}

const { TextArea } = Input;

const CloseJobModal: React.FC<CloseJobModalProps> = ({
  open,
  job,
  loading,
  onCancel,
  onCloseJob,
}) => {
  const [reason, setReason] = useState("");
  const [closeOnExternal, setCloseOnExternal] = useState(true);

  useEffect(() => {
    if (open) {
      setReason("");
      setCloseOnExternal(true);
    }
  }, [open]);

  const handleOk = () => {
    if (job) {
      onCloseJob(job.id, reason, closeOnExternal);
    }
  };

  const hasExternalPublishings = React.useMemo(() => {
    if (!job || !job.platforms) return false;
    return job.platforms.some((p: any) => p.status === "published" && p.platform !== "career_page");
  }, [job]);

  return (
    <Modal
      title={
        <span className="d-flex align-items-center text-danger">
          <i className="ti ti-lock me-2 fs-16" />
          Close Job Posting
        </span>
      }
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Close Job"
      okButtonProps={{ danger: true }}
      confirmLoading={loading}
      centered
      width={480}
      keyboard={false}
      zIndex={1001}
      styles={{
        mask: {
          position: "fixed", inset: 0, width: "100vw", height: "100vh",
          backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0, 0, 0, 0.45)",
        },
      }}
    >
      <div className="py-2">
        {job && (
          <div className="mb-4">
            <h6 className="fw-semibold mb-1 text-truncate">{job.job_title}</h6>
            <p className="text-muted small mb-0">
              This will stop accepting new applications and mark the job as closed.
            </p>
          </div>
        )}

        <Form layout="vertical">
          <Form.Item label="Reason for closing (Optional)">
            <TextArea
              rows={3}
              placeholder="E.g., Position filled, Cancelled search..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Form.Item>

          {hasExternalPublishings && (
            <Form.Item className="mb-0">
              <div
                className="d-flex align-items-start gap-3 p-3 rounded"
                style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
              >
                <Switch
                  checked={closeOnExternal}
                  onChange={setCloseOnExternal}
                  size="small"
                  className="mt-1"
                />
                <div>
                  <div className="fw-medium text-dark" style={{ fontSize: 13.5 }}>
                    Unpublish from external platforms
                  </div>
                  <div className="text-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                    Automatically removes this job posting from LinkedIn, Indeed, etc.
                  </div>
                </div>
              </div>
            </Form.Item>
          )}
        </Form>
      </div>
    </Modal>
  );
};

export default CloseJobModal;
